import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, CheckCircle, Building2, User, Sparkles, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { motion } from "framer-motion";

const sponsorshipSchema = z.object({
  requesterName: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
  requesterEmail: z.string().trim().email("Invalid email address").max(255),
  requesterPhone: z.string().trim().max(20).optional(),
  companyName: z.string().trim().min(1, "Company name is required").max(100),
  sponsorshipInterest: z.string().min(1, "Please describe your interest").max(2000),
});

interface EventOrganizer {
  id: string;
  name: string;
  company_name: string | null;
  contact_info: { email?: string; phone?: string } | null;
  sponsorship_packages: Array<{ name: string; price: number; benefits: string[] }> | null;
}

const SponsorEvent = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const isRTL = language === 'ar';
  
  const [event, setEvent] = useState<EventOrganizer | null>(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Form state
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [requesterPhone, setRequesterPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("");
  const [sponsorshipInterest, setSponsorshipInterest] = useState("");

  // Auto-fill user data when logged in
  useEffect(() => {
    if (user) {
      if (!requesterEmail) setRequesterEmail(user.email || "");
      if (!requesterName) {
        const name = user.user_metadata?.full_name || user.user_metadata?.name || "";
        setRequesterName(name);
      }
    }
  }, [user]);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;

      try {
        const { data, error } = await supabase
          .from('event_organizers')
          .select('id, name, company_name, contact_info, sponsorship_packages')
          .eq('id', eventId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setEvent({
            id: data.id,
            name: data.name,
            company_name: data.company_name,
            contact_info: data.contact_info as EventOrganizer['contact_info'],
            sponsorship_packages: data.sponsorship_packages as EventOrganizer['sponsorship_packages'],
          });
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      } finally {
        setEventLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  // Pre-select package from URL
  useEffect(() => {
    const packageParam = searchParams.get("package");
    if (packageParam && event?.sponsorship_packages) {
      const found = event.sponsorship_packages.find(p => p.name === packageParam);
      if (found) {
        setSelectedPackage(found.name);
      }
    }
  }, [searchParams, event]);

  const validateForm = () => {
    try {
      sponsorshipSchema.parse({
        requesterName,
        requesterEmail,
        requesterPhone: requesterPhone || undefined,
        companyName,
        sponsorshipInterest,
      });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!eventId || !event) return;

    setIsSubmitting(true);

    try {
      const pkg = event.sponsorship_packages?.find(p => p.name === selectedPackage);

      // Save sponsorship booking to database
      const { error: dbError } = await supabase.from("sponsorship_bookings").insert({
        event_id: eventId,
        requester_id: user?.id || null,
        requester_name: requesterName.trim(),
        requester_email: requesterEmail.trim(),
        requester_phone: requesterPhone.trim() || null,
        company_name: companyName.trim(),
        selected_package: selectedPackage || null,
        package_price: pkg?.price || null,
        sponsorship_interest: sponsorshipInterest.trim(),
        status: "pending",
      });

      if (dbError) throw dbError;

      // Send notifications (non-blocking, parallel)
      const notificationPromises = [
        // Notify event organizer
        supabase.functions.invoke("send-sponsorship-notification", {
          body: {
            eventId,
            eventName: event.name,
            organizerEmail: event.contact_info?.email,
            requesterName: requesterName.trim(),
            requesterEmail: requesterEmail.trim(),
            requesterPhone: requesterPhone.trim() || undefined,
            companyName: companyName.trim(),
            selectedPackage: selectedPackage || undefined,
            packagePrice: pkg?.price,
            sponsorshipInterest: sponsorshipInterest.trim(),
          },
        }),
        // Send confirmation to requester
        supabase.functions.invoke("send-booking-confirmation", {
          body: {
            type: "sponsorship",
            requesterName: requesterName.trim(),
            requesterEmail: requesterEmail.trim(),
            targetName: event.name,
            companyName: companyName.trim(),
            selectedPackage: selectedPackage || undefined,
            budget: pkg?.price || undefined,
            dashboardUrl: user ? `${window.location.origin}/dashboard` : undefined,
          },
        }),
      ];

      Promise.all(notificationPromises).catch(err => 
        console.error("Email notification error:", err)
      );

      setIsSuccess(true);
      toast({
        title: isRTL ? 'تم إرسال الطلب!' : 'Request Submitted!',
        description: isRTL 
          ? 'سيقوم منظم الفعالية بمراجعة طلبك والتواصل معك قريباً.'
          : 'The event organizer will review your request and get back to you soon.',
      });
    } catch (error: any) {
      console.error("Sponsorship request error:", error);
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: isRTL 
          ? 'فشل في إرسال الطلب. يرجى المحاولة مرة أخرى.'
          : 'Failed to submit request. Please try again.',
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (eventLoading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">
          {isRTL ? 'لم يتم العثور على الفعالية' : 'Event Not Found'}
        </h1>
        <Button asChild variant="outline">
          <Link to="/events-sponsors">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isRTL ? 'العودة للفعاليات' : 'Back to Events'}
          </Link>
        </Button>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <motion.div 
        className="container mx-auto px-4 py-16 max-w-lg text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 border border-emerald-500/20">
          <div className="h-16 w-16 mx-auto mb-6 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold mb-3">
            {isRTL ? 'تم إرسال الطلب!' : 'Request Submitted!'}
          </h1>
          <p className="text-muted-foreground mb-2">
            {isRTL 
              ? `تم إرسال طلب الرعاية الخاص بك لـ ${event.name}.`
              : `Your sponsorship request for ${event.name} has been sent.`}
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            {isRTL 
              ? "تم إرسال تأكيد إلى بريدك الإلكتروني."
              : "A confirmation has been sent to your email."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild variant="outline">
              <Link to={`/events/${eventId}`}>
                {isRTL ? 'عرض الفعالية' : 'View Event'}
              </Link>
            </Button>
            <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
              <Link to="/events-sponsors">
                {isRTL ? 'تصفح المزيد من الفعاليات' : 'Browse More Events'}
              </Link>
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  const selectedPkg = event.sponsorship_packages?.find(p => p.name === selectedPackage);

  return (
    <motion.div 
      className="container mx-auto px-4 py-12 max-w-3xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Back Button */}
      <div className="mb-6">
        <Button variant="outline" asChild className="border-primary/10 hover:bg-primary/5">
          <Link to={`/events/${eventId}`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {isRTL ? 'العودة للفعالية' : 'Back to Event'}
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {isRTL ? `كن راعياً لـ ${event.name}` : `Sponsor ${event.name}`}
        </h1>
        <p className="text-muted-foreground">
          {isRTL 
            ? 'أكمل النموذج أدناه لإرسال طلب رعاية.'
            : 'Fill out the form below to submit a sponsorship request.'}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* User logged in indicator */}
          {user && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-emerald-500/5 p-3 rounded-lg border border-emerald-500/10">
              <User className="h-4 w-4 text-emerald-600" />
              <span>
                {isRTL 
                  ? `مسجل الدخول باسم ${user.email}`
                  : `Logged in as ${user.email}`}
              </span>
              <Badge variant="secondary" className="ml-auto bg-emerald-500/10 text-emerald-600">
                <Sparkles className="h-3 w-3 mr-1" />
                {isRTL ? "تم الملء التلقائي" : "Auto-filled"}
              </Badge>
            </div>
          )}

          {/* Contact Information */}
          <Card className="border-emerald-500/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {isRTL ? 'معلوماتك' : 'Your Information'}
              </CardTitle>
              <CardDescription>
                {isRTL ? "أدخل تفاصيل الاتصال الخاصة بك" : "Enter your contact details"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{isRTL ? 'الاسم الكامل *' : 'Full Name *'}</Label>
                  <Input
                    id="name"
                    value={requesterName}
                    onChange={(e) => setRequesterName(e.target.value)}
                    placeholder={isRTL ? 'اسمك' : 'Your name'}
                    className={errors.requesterName ? "border-destructive" : ""}
                  />
                  {errors.requesterName && (
                    <p className="text-sm text-destructive">{errors.requesterName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{isRTL ? 'البريد الإلكتروني *' : 'Email *'}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={requesterEmail}
                    onChange={(e) => setRequesterEmail(e.target.value)}
                    placeholder="your@email.com"
                    className={errors.requesterEmail ? "border-destructive" : ""}
                  />
                  {errors.requesterEmail && (
                    <p className="text-sm text-destructive">{errors.requesterEmail}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">{isRTL ? 'رقم الهاتف' : 'Phone Number'}</Label>
                  <Input
                    id="phone"
                    value={requesterPhone}
                    onChange={(e) => setRequesterPhone(e.target.value)}
                    placeholder="+966 5XX XXX XXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">{isRTL ? 'اسم الشركة *' : 'Company Name *'}</Label>
                  <Input
                    id="company"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={isRTL ? 'اسم شركتك' : 'Your company name'}
                    className={errors.companyName ? "border-destructive" : ""}
                  />
                  {errors.companyName && (
                    <p className="text-sm text-destructive">{errors.companyName}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sponsorship Details */}
          <Card className="border-emerald-500/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {isRTL ? 'تفاصيل الرعاية' : 'Sponsorship Details'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {event.sponsorship_packages && event.sponsorship_packages.length > 0 && (
                <div className="space-y-2">
                  <Label>{isRTL ? 'اختر حزمة الرعاية' : 'Select Sponsorship Package'}</Label>
                  <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                    <SelectTrigger>
                      <SelectValue placeholder={isRTL ? 'اختر حزمة' : 'Choose a package'} />
                    </SelectTrigger>
                    <SelectContent>
                      {event.sponsorship_packages.map((pkg, index) => (
                        <SelectItem key={index} value={pkg.name}>
                          {pkg.name} - {pkg.price.toLocaleString()} SAR
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Selected Package Summary */}
              {selectedPkg && (
                <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{selectedPkg.name}</span>
                    <Badge className="bg-emerald-600">{selectedPkg.price.toLocaleString()} SAR</Badge>
                  </div>
                  {selectedPkg.benefits && selectedPkg.benefits.length > 0 && (
                    <ul className="space-y-1 mt-3">
                      {selectedPkg.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="interest">
                  {isRTL ? 'اهتماماتك بالرعاية *' : 'Sponsorship Interest *'}
                </Label>
                <Textarea
                  id="interest"
                  value={sponsorshipInterest}
                  onChange={(e) => setSponsorshipInterest(e.target.value)}
                  placeholder={isRTL 
                    ? 'صف اهتمامك بالرعاية وما تأمل تحقيقه...'
                    : 'Describe your interest in sponsoring and what you hope to achieve...'}
                  rows={4}
                  className={errors.sponsorshipInterest ? "border-destructive" : ""}
                />
                {errors.sponsorshipInterest && (
                  <p className="text-sm text-destructive">{errors.sponsorshipInterest}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:shadow-lg transition-all"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isRTL ? 'جاري الإرسال...' : 'Submitting...'}
              </>
            ) : (
              <>
                <Building2 className="mr-2 h-4 w-4" />
                {isRTL ? 'إرسال طلب الرعاية' : 'Submit Sponsorship Request'}
              </>
            )}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default SponsorEvent;
